import { RobotTypeEnum } from 'api/schema'
import { findLotModelByNumber } from 'utils/stationLotModel'

export enum LotType {
  NOT_SET = 0,
  WASHING = 1,
  CHARGING = 2,
  PARKING = 3,
  REFILLING = 4,
  DEBRIS_DUMP = 5,
}

export enum ActionType {
  NOT_SET = 0,
  WAITING = 1,
  PARKING = 2,
  WASHING = 3,
  CHARGING = 4,
  REFILLING = 5,
  WASTE_WATER_DRAIN = 6,
  DEBRIS_DUMP = 7,
}

export const ACTION_TYPE_LABELS: Record<number, string> = {
  [ActionType.NOT_SET]: 'Not Set',
  [ActionType.WAITING]: 'Waiting',
  [ActionType.PARKING]: 'Parking',
  [ActionType.WASHING]: 'Washing',
  [ActionType.CHARGING]: 'Charging',
  [ActionType.REFILLING]: 'Refilling',
  [ActionType.WASTE_WATER_DRAIN]: 'Waste Water Drain',
  [ActionType.DEBRIS_DUMP]: 'Debris Dump',
}

export interface ILotModelConfig {
  id: number
  model_number: string
  name: string
  allowed_actions: ActionType[]
  type: LotType
}

export const SERVICE_POINT_KINDS = {
  WAIT: 'wait',
  STATION_LOT: 'station_lot',
  MAINTENANCE_POINT: 'maintenance_point',
} as const

export type ServicePointKind = typeof SERVICE_POINT_KINDS[keyof typeof SERVICE_POINT_KINDS]

export function isSweeperRobotType(robotType?: string | null): boolean {
  return (robotType || '').trim() === RobotTypeEnum.SWEEPER
}

export function filterCatalogActionsForRobotType(
  actions: ActionType[],
  robotType?: string | null
): ActionType[] {
  if (!isSweeperRobotType(robotType)) {
    return actions
  }
  return actions.filter(action => action !== ActionType.WASTE_WATER_DRAIN)
}

export function isDrainOnlyMaintenancePointType(type?: string | null): boolean {
  return type === 'waste_water_drain'
}

export interface IServicePoint {
  kind: ServicePointKind
  position: number
  order: number
  action_type: ActionType[]
  time_sec?: number
  station_id?: string
  lot_id?: number
  lot_pk?: number
  lot_model?: string
  maintenance_point_id?: number
}

type MaintenancePointRef = { facility_index?: number | null; type?: string }

/** Scenario editor: allowed action chips for a station lot row (CHARGING only at scenario end). */
export function resolveScenarioStationLotAllowedActions(
  modelConfig: ILotModelConfig,
  options: { isLast: boolean; hasChargingLotInScenario: boolean; robotType?: string | null }
): ActionType[] {
  let allowed = [...(modelConfig.allowed_actions ?? [])]
  if (modelConfig.type === LotType.REFILLING && options.hasChargingLotInScenario) {
    allowed = allowed.filter(action => action !== ActionType.CHARGING)
  }
  if (!options.isLast) {
    allowed = allowed.filter(action => action !== ActionType.CHARGING)
  }
  return filterCatalogActionsForRobotType(allowed, options.robotType)
}

/** After stripping CHARGING off-scenario-end, auto-pick the sole remaining action (REFILLING lot). */
export function autoFillScenarioActionsAfterChargingStrip(
  sp: IServicePoint,
  actions: ActionType[],
  lotModels: ILotModelConfig[],
  isLast: boolean,
  robotType?: string | null
): ActionType[] {
  if (actions.length > 0 || isLast || sp.kind !== SERVICE_POINT_KINDS.STATION_LOT || !sp.lot_model) {
    return actions
  }
  const modelConfig = findLotModelByNumber(lotModels, sp.lot_model)
  if (!modelConfig || modelConfig.type !== LotType.REFILLING) {
    return actions
  }
  const remaining = filterCatalogActionsForRobotType(
    (modelConfig.allowed_actions ?? []).filter(action => action !== ActionType.CHARGING),
    robotType
  )
  if (remaining.length === 1) {
    return [remaining[0]]
  }
  return actions
}

/** Scenario editor: user may clear all action chips; empty drafts are persisted (UX-2). */
export function allowsEmptyScenarioActionSelection(
  sp: IServicePoint,
  lotModels: ILotModelConfig[],
  maintenancePoints: MaintenancePointRef[],
  options: { isLast: boolean; hasChargingLotInScenario: boolean; robotType?: string | null }
): boolean {
  if (sp.kind === SERVICE_POINT_KINDS.WAIT) {
    return false
  }
  if (sp.kind === SERVICE_POINT_KINDS.MAINTENANCE_POINT) {
    const mp = maintenancePoints.find(p => p.facility_index === sp.maintenance_point_id)
    if (mp?.type === 'universal') {
      return true
    }
    return isSweeperRobotType(options.robotType) && isDrainOnlyMaintenancePointType(mp?.type)
  }
  if (sp.kind === SERVICE_POINT_KINDS.STATION_LOT) {
    const modelConfig = sp.lot_model ? findLotModelByNumber(lotModels, sp.lot_model) : null
    if (!modelConfig) {
      return false
    }
    if (modelConfig.type === LotType.REFILLING) {
      return true
    }
    const allowed = resolveScenarioStationLotAllowedActions(modelConfig, options)
    return allowed.length > 1
  }
  return false
}

export function legacyToServicePoints(maintenance: any, missionCount: number): IServicePoint[] {
  if (!maintenance) return []
  if (maintenance.service_points) {
    return maintenance.service_points.map((sp: any) => ({
      ...sp,
      action_type: (sp.action_type || []).map((a: any) =>
        typeof a === 'string' ? ActionType[a as keyof typeof ActionType] ?? a : a
      ),
    }))
  }

  const steps: IServicePoint[] = []

  const waitingPoints = maintenance.waiting_points || []
  waitingPoints.forEach((wp: any, idx: number) => {
    steps.push({
      kind: SERVICE_POINT_KINDS.WAIT,
      position: wp.position,
      order: idx,
      action_type: [ActionType.WAITING],
      time_sec: wp.time,
    })
  })

  return steps
}
